import React, { Component } from 'react';
import { array, arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { FormattedMessage, intlShape, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { parse, stringify } from '../../../util/urlHelpers';
import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';
import {
  abilityToSell,
  communityInviiteRoute,
  communityPostAccess,
  communityGorupAccess,
  CURRENT_USER_BUSINESS_TYPE,
  LOCAL_STORAGE_CIRCLE_AUTH_TOKEN,
  nonVerifyEamilRestrictedRoutes,
  propTypes,
  SIGNUP_TAB_DETAIL,
} from '../../../util/types';
import {
  Avatar,
  Button,
  IconProfileCard,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
  NamedLink,
  NamedRedirect,
  CompleteBusinessProfileModal,
} from '../../../components';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop, { ProfileMenu } from './TopbarDesktop/TopbarDesktop';
import ProfileIconCard from '../../../components/ProfileIconCard/ProfileIconCard';
import css from './Topbar.module.css';
import PasswordWallForm from './PasswordWallForm/PasswordWallForm';
import {
  cartItemsCount,
  currentUserRole,
  ensureCurrentUser,
  getUserPermissions,
} from '../../../util/data';
import { openIntecomOnClickEvents } from '../../../components/IntercomComponent/IntercomComponent';
import ElephantVerificationForm from './ElephantVerificationForm/ElephantVerificationForm';
import MobileHamburgerNavigation from './MobileHamburgerNavigation/MobileHamburgerNavigation';
import BookmarkModal from '../../CircleCommunityPage/components/BookmarkModal/BookmarkModal';
import MyActivityModal from '../../CircleCommunityPage/components/MyActivity/MyActivityModal';
import { onCreateIntercomTicket, onSingleMailApiCollection } from '../../../util/api';
import RequestSpaceModal from '../../CircleCommunityPage/components/SpaceGroups/RequestSpaceModal';
import { toastifyFn } from '../../../components/ReactToastify/ReactToastify';
import RequestRuleBookForm from '../../PageBuilder/SectionBuilder/SectionHero/RequestRuleBookForm';
import NotificationMobileModal from '../../../components/NotificationDropdown/NotificationMobileModal';
import { Redirect } from 'react-router-dom';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

const redirectToURLWithModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const toNotificationCount = value => {
  const count = Number(value);
  return Number.isFinite(count) ? count : 0;
};

const getUnreadNotificationCountFromList = notificationsData => {
  const { records = [], mongoNotifications = [] } = notificationsData || {};
  const circleUnreadCount = Array.isArray(records)
    ? records.filter(item => !item?.read_at).length
    : 0;
  const mongoUnreadCount = Array.isArray(mongoNotifications)
    ? mongoNotifications.filter(item => item?.read === false).length
    : 0;

  return circleUnreadCount + mongoUnreadCount;
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
export const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
export const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return (
    <div className={classes}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

GenericError.propTypes = {
  show: bool.isRequired,
};

class TopbarComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isSiderbarOpen: false,
      isBookmarkListRequested: false,
      isMyActivityModalOpen: false,
      loading: false,
      requestRuleBookModal: {
        isModalOpen: false,
        inProgress: false,
        isError: null,
      },
      isOpenNotificationModal: false,
      businessProfileModalOpen: false,
      isMobileMenuOpen: false,
      savedScrollPosition: 0,
    };
    this.handleMobileMenuOpen = this.handleMobileMenuOpen.bind(this);
    this.handleMobileMenuClose = this.handleMobileMenuClose.bind(this);
    this.handleMobileSearchOpen = this.handleMobileSearchOpen.bind(this);
    this.handleMobileSearchClose = this.handleMobileSearchClose.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.onRequestSpace = this.onRequestSpace.bind(this);
    this.requestRuleBookSubmit = this.requestRuleBookSubmit.bind(this);
    this.handleCloseBusinessProfileModal = this.handleCloseBusinessProfileModal.bind(this);
    this.saveScrollPosition = this.saveScrollPosition.bind(this);
    this.restoreScrollPosition = this.restoreScrollPosition.bind(this);
  }

  saveScrollPosition() {
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    this.setState({ savedScrollPosition: scrollPosition });
  }

  restoreScrollPosition() {
    // Only restore scroll on mobile devices (where the issue occurs)
    if (typeof window !== 'undefined' && window.innerWidth <= 1024) {
      // Try to get scroll position from sessionStorage first (set by ProductOrderForm)
      let scrollPosition = this.state.savedScrollPosition;
      const savedPosition = window.sessionStorage.getItem('topbarModalScrollPosition');
      if (savedPosition) {
        scrollPosition = parseInt(savedPosition, 10);
        window.sessionStorage.removeItem('topbarModalScrollPosition');
      }
      setTimeout(() => {
        window.scrollTo({ top: scrollPosition, behavior: 'instant' });
      }, 0);
    }
  }

  handleMobileMenuOpen() {
    redirectToURLWithModalState(this.props, 'mobilemenu');
  }

  handleMobileMenuClose() {
    redirectToURLWithoutModalState(this.props, 'mobilemenu');
  }

  handleMobileSearchOpen() {
    redirectToURLWithModalState(this.props, 'mobilesearch');
  }

  handleMobileSearchClose() {
    redirectToURLWithoutModalState(this.props, 'mobilesearch');
  }

  handleCloseBusinessProfileModal() {
    // Mark that the modal has been shown to prevent showing again on next login
    if (this.props.onBusinessProfileUpdate) {
      this.props.onBusinessProfileUpdate({
        publicData: {
          isCompleteProfileModalShown: true,
        },
      });
    }
    redirectToURLWithoutModalState(this.props, 'isOpenbussinessModal');
  }

  handleSubmit(values) {
    const { currentSearchParams } = this.props;
    const { history, config, routeConfiguration } = this.props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  }

  handleLogout() {
    const { onLogout, history, routeConfiguration } = this.props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      try {
        typeof window !== 'undefined' && localStorage.removeItem('authCred');
        typeof window !== 'undefined' && localStorage.removeItem(LOCAL_STORAGE_CIRCLE_AUTH_TOKEN);
      } catch (e) {
        console.log(e, '&&& &&& => e');
      }

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  }

  async onRequestSpace(values) {
    const { onRequestGroupClick } = this.props;
    try {
      this.setState({ loading: true });
      const intercomTicketPromise = await onCreateIntercomTicket(values);
      toastifyFn('success', 'Request sent successfully');
      return intercomTicketPromise;
    } catch (error) {
      toastifyFn('error', 'Oops, something went wrong');
      console.error(error, '&&& &&& => error');
    } finally {
      this.setState({ loading: false });
      onRequestGroupClick(false);
    }
  }

  async requestRuleBookSubmit(values, form) {
    const { currentUser, intl } = this.props;
    this.setState({
      requestRuleBookModal: {
        ...this.state.requestRuleBookModal,
        inProgress: true,
      },
    });

    try {
      const { email, profile } = currentUser?.attributes || {};
      const { firstName = '', lastName = '' } = profile || {};

      const data = {
        ...values,
        userName: `${firstName} ${lastName}`,
        userEmail: email,
        email: process.env.REACT_APP_SENDGRID_ADMIN_RECIVER_EMAIL,
        name: process.env.REACT_APP_SENDGRID_SENDER_NAME,
        userId: currentUser?.id?.uuid,
        groupName: values.rulebook || '',
        viewRequestURL: '',
      };

      await onSingleMailApiCollection({
        data,
        template_id: REQUEST_RULEBOOK_TEMPLATE_ID,
      });

      toastifyFn('success', intl.formatMessage({ id: 'RequestRulebookForm.submitSuccess' }));
    } catch (error) {
      console.error('Error submitting request rulebook:', error);
      toastifyFn('error', intl.formatMessage({ id: 'RequestRulebookForm.submitError' }));
    } finally {
      this.setState({
        requestRuleBookModal: {
          ...this.state.requestRuleBookModal,
          isModalOpen: false,
          inProgress: false,
        },
      });
    }
  }

  render() {
    const {
      className,
      rootClassName,
      desktopClassName,
      mobileRootClassName,
      mobileClassName,
      isAuthenticated,
      isLoggedInAs,
      authScopes,
      authInProgress,
      currentUser,
      currentUserHasListings,
      currentUserHasOrders,
      currentPage,
      notificationCount,
      intl,
      location,
      onManageDisableScrolling,
      onResendVerificationEmail,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      showGenericError,
      config,
      routeConfiguration,
      marketplaceCommingSoonRequest,
      onMarketplaceCommingSoonRequest,
      restrictMarketplcaeRequest,
      onRestrictMarketplcaeRequest,
      subscriptionPermissionRequest,
      isAddAnItemPage,
      onSubscriptionPermissionRequest,
      requestGroupPermissionModal,
      onRequestGroupPermissionModal,
      onFetchNotifications,
      onSuccessNotifications,
      requestNotificationsInProgress,
      notificationsData,
      onFetchNotificationsCount,
      onMarkNotificationRead,
      notificationCountData,
      onResetNotificationsCount,
      onMarkAllNotificationsAsRead,
      onGetNotificationPreference,
      requestNotificationPreferenceInProgress,
      notificaitonPreferenceData,
      onUpdateNotificationPreference,
      history,
      currentUserOrderNotificationCount = 0,
      elephantVerificationModal = false,
      onReevaluateElephantVerification,
      onSetElephantVerificationModal,
      elephantVerificationInProgress,
      elephantVerificationError,
      spaceListData,
      userModeratorsGroups,
      requestBookmarksList,
      bookmarksListData,
      onDeleteBookMark,
      currentCommunityMember,
      onFetchUserActivity,
      fetchUserActivityRequest,
      fetchUserActivityData,
      fetchUserActivityError,
      requestNewSpace,
      isRequestSpace,
      onRequestGroupClick,
      userFavChatDetails,
      chatDetails,
      onToggleCreatePost,
    } = this.props;
    const { cart } = currentUser?.attributes.profile.privateData || {};
    const cartCount = cartItemsCount(cart) || 0;
    const userSubscriptionRole = currentUserRole(currentUser);

    // <<<<<<<<<<<<< all access checked by this fn >>>>>>>>>>>>>>>>>>>>>
    // communityPostAccess >> user can create post or not
    const userAbilityToSell = getUserPermissions(userSubscriptionRole, abilityToSell);
    const checkCreatePostAuth = getUserPermissions(userSubscriptionRole, communityPostAccess);
    const checkCreateGroupAuth = getUserPermissions(userSubscriptionRole, communityGorupAccess);

    const profileMenuMaybe = true ? (
      <ProfileMenu
        currentPage={currentPage}
        currentUser={currentUser}
        isAuthenticated={isAuthenticated}
        onLogout={this.handleLogout}
        cartNotificationCount={cartCount}
        inboxNotificationCount={notificationCount}
        userAbilityToSell={userAbilityToSell}
        currentUserOrderNotificationCount={currentUserOrderNotificationCount}
      />
    ) : null;

    const {
      mobilemenu,
      mobilesearch,
      keywords,
      address,
      origin,
      bounds,
      isOpenbussinessModal,
    } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    // Custom links are sorted so that group="primary" are always at the beginning of the list.
    const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
    const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
    const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

    const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

    const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
    const isMobileLayout = hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : true;
    const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
    const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';
    const handleMobileNotificationOpen = () => {
      if (isMobileMenuOpen) {
        redirectToURLWithoutModalState(this.props, 'mobilemenu');
      }
      onFetchNotifications({ forceFresh: true });
      this.setState({
        isOpenNotificationModal: true,
        isSiderbarOpen: false,
      });
      onResetNotificationsCount();
    };

    const mobileMenu = (
      <TopbarMobileMenu
        isAuthenticated={isAuthenticated}
        currentUserHasListings={currentUserHasListings}
        currentUser={currentUser}
        onLogout={this.handleLogout}
        notificationCount={notificationCount}
        currentPage={resolvedCurrentPage}
        customLinks={customLinks}
      />
    );

    const topbarSearcInitialValues = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords };
      }

      // Only render current search if full place object is available in the URL params
      const locationFieldsPresent = isOriginInUse(config)
        ? address && origin && bounds
        : address && bounds;
      return {
        location: locationFieldsPresent
          ? {
              search: address,
              selectedPlace: { address, origin, bounds },
            }
          : null,
      };
    };
    const initialSearchFormValues = topbarSearcInitialValues();

    const classes = classNames(rootClassName || css.root, className);

    const user = ensureCurrentUser(currentUser);
    const { accountActive = true } = (user && user.id && user.attributes.profile.publicData) || {};
    const currentUserLoaded = !!user.id;
    const showEmailVerification = currentUserLoaded && !user.attributes.emailVerified;
    const isSignUpStepsPage =
      typeof window != 'undefined' && nonVerifyEamilRestrictedRoutes.includes(currentPage);
    const isSignupReturnURL = communityInviiteRoute.includes(currentPage);
    const signupRedirectUrl = !!user?.id
      ? user?.attributes?.profile?.publicData?.signupRedirectUrl
      : null;
    // http://localhost:3000/feed-page/my-group/c/ms-testing-4/2065416
    // // redirect user to signup step page
    if (showEmailVerification && isSignUpStepsPage && typeof window != 'undefined') {
      console.log(location?.pathname, '&&& &&& => location?.pathname');
      if (location?.pathname == '/account/contact-details') {
      } else {
        return <NamedRedirect name="SignUpStepsPage" params={{ tab: SIGNUP_TAB_DETAIL }} />;
      }
    }
    const platformRestrictedUser = false;
    // user && user.id ? (!!user?.attributes?.profile?.privateData?.userDeniedByAdmin) : false;

    const phoneNumber = user?.attributes?.profile?.protectedData?.phoneNumber || null;
    const { new_notifications_count = 0, new_mentions_count = 0 } = notificationCountData || {};
    const totalMobileNotificationCount = Math.max(
      toNotificationCount(new_notifications_count) + toNotificationCount(new_mentions_count),
      getUnreadNotificationCountFromList(notificationsData)
    );

    // Check if user is a business user for business profile modal
    const currentUserType = user?.attributes?.profile?.publicData?.currentUserType;
    const isBusinessUser = currentUserType === CURRENT_USER_BUSINESS_TYPE;

    const { bio, publicData } = user?.attributes?.profile || {};
    const {
      storeName,
      businessAddress,
      primaryBusinessCategory,
      zipCode,
      isCompleteProfileModalShown,
    } = publicData || {};
    const emailVerified = user?.attributes?.emailVerified;

    // Check which fields are missing
    const hasProfileImage = !!user.profileImage?.id;
    const hasStoreName = !!storeName;
    const hasBusinessAddress = !!businessAddress;
    const hasZipCode = !!zipCode;
    const hasBio = !!bio;
    const hasEmailVerified = !!emailVerified;
    const hasPrimaryBusinessCategory = !!primaryBusinessCategory?.value;

    // Check if profile is complete (all required fields filled)
    const isProfileComplete =
      hasProfileImage &&
      hasStoreName &&
      hasBusinessAddress &&
      hasZipCode &&
      hasBio &&
      hasEmailVerified &&
      hasPrimaryBusinessCategory;

    // Show modal:
    // 1. When URL param isOpenbussinessModal=true (manual trigger)
    // 2. OR when business user logs in for the first time (isCompleteProfileModalShown is not set)
    // 3. NOT on /welcome/detail route (has another modal)
    const isModalOpenedViaUrl = isOpenbussinessModal === 'true' || isOpenbussinessModal === true;
    const shouldShowOnFirstLogin =
      !isCompleteProfileModalShown && isBusinessUser && !isProfileComplete;
    const isWelcomeDetailPage =
      resolvedCurrentPage === 'SignUpStepsPage' || location.pathname?.includes('/welcome/detail');
    const showBusinessProfileModal =
      currentUserLoaded &&
      isBusinessUser &&
      !isWelcomeDetailPage &&
      (isModalOpenedViaUrl || shouldShowOnFirstLogin);
    return (
      <div className={classes}>
        <LimitedAccessBanner
          isAuthenticated={isAuthenticated}
          isLoggedInAs={isLoggedInAs}
          authScopes={authScopes}
          currentUser={currentUser}
          onLogout={this.handleLogout}
          currentPage={resolvedCurrentPage}
        />
        <div className={classNames(mobileRootClassName || css.container, mobileClassName)}>
          <div className={css.mobileMenuIcon}>
            <MobileHamburgerNavigation
              currentUser={currentUser}
              notificationCount={notificationCount + currentUserOrderNotificationCount || 0}
              currentUserUnreadInquiriesCount={this.props.currentUserUnreadInquiriesCount}
              cartCount={cartCount}
              history={history}
              routeConfiguration={routeConfiguration}
              onLogout={this.handleLogout}
              userModeratorsGroups={userModeratorsGroups}
              spaceListData={spaceListData}
              setIsBookmarkListRequested={value => {
                this.setState({ isBookmarkListRequested: value });
              }}
              currentCommunityMember={currentCommunityMember}
              setIsMyActivityModalOpen={value => {
                this.setState({ isMyActivityModalOpen: value });
              }}
              checkCreatePostAuth={checkCreatePostAuth}
              checkCreateGroupAuth={checkCreateGroupAuth}
              onRequestGroupClick={onRequestGroupClick}
              onOpenRuleBookModal={() => {
                this.setState({
                  requestRuleBookModal: {
                    isModalOpen: true,
                    inProgress: false,
                    isError: null,
                  },
                });
              }}
              userFavChatDetails={userFavChatDetails}
              chatDetails={chatDetails}
              openNotificationModal={handleMobileNotificationOpen}
              notificationCountData={notificationCountData}
              userAbilityToSell={userAbilityToSell}
              currentPage={currentPage}
              onFetchNotificationsCount={onFetchNotificationsCount}
              onToggleCreatePost={onToggleCreatePost}
              isSiderbarOpen={this.state.isSiderbarOpen}
              onSiderbarChange={isSiderbarOpen => this.setState({ isSiderbarOpen })}
            />{' '}
          </div>
          <LinkedLogo
            className={css.mobileLogo}
            layout={'mobile'}
            alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
            linkToExternalSite={config?.topbar?.logoLink}
          />
          <div className={css.actionButton}>
            {isAuthenticated ? (
              <button
                type="button"
                className={css.mobileNotificationButton}
                onClick={handleMobileNotificationOpen}
                aria-label={intl.formatMessage({ id: 'MobileBottomTabs.notifications' })}
              >
                <ProfileIconCard type="notification_mobile" />
                {totalMobileNotificationCount > 0 ? (
                  <span className={css.mobileNotificationBadge}>
                    {totalMobileNotificationCount}
                  </span>
                ) : null}
              </button>
            ) : null}
            {/* <Button
              rootClassName={css.searchMenu}
              // onClick={this.handleMobileSearchOpen}
              onClick={() => {
                onMarketplaceCommingSoonRequest(true);
              }}
              title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
            >
              <SearchIcon className={css.searchMenuIcon} />
            </Button> */}
            {/* <Button
              className={css.cartIcon}
              onClick={() => {
                onMarketplaceCommingSoonRequest(true);
              }}
            >
              <ProfileIconCard type="cart_mobile" />
            </Button> */}
          </div>
        </div>
        <div className={css.desktop}>
          <TopbarDesktop
            className={desktopClassName}
            currentUserHasListings={currentUserHasListings}
            currentUser={currentUser}
            currentPage={resolvedCurrentPage}
            initialSearchFormValues={initialSearchFormValues}
            intl={intl}
            isAuthenticated={isAuthenticated}
            notificationCount={notificationCount}
            onLogout={this.handleLogout}
            onSearchSubmit={this.handleSubmit}
            config={config}
            customLinks={customLinks}
            onFetchNotifications={onFetchNotifications}
            onSuccessNotifications={onSuccessNotifications}
            isMobileLayout={isMobileLayout}
            requestNotificationsInProgress={requestNotificationsInProgress}
            notificationsData={notificationsData}
            onFetchNotificationsCount={onFetchNotificationsCount}
            notificationCountData={notificationCountData}
            onMarkNotificationRead={onMarkNotificationRead}
            onResetNotificationsCount={onResetNotificationsCount}
            onMarkAllNotificationsAsRead={onMarkAllNotificationsAsRead}
            onGetNotificationPreference={onGetNotificationPreference}
            requestNotificationPreferenceInProgress={requestNotificationPreferenceInProgress}
            notificaitonPreferenceData={notificaitonPreferenceData}
            onUpdateNotificationPreference={onUpdateNotificationPreference}
            restrictMarketplcaeRequest={restrictMarketplcaeRequest}
            currentUserOrderNotificationCount={currentUserOrderNotificationCount}
            currentUserUnreadInquiriesCount={this.props.currentUserUnreadInquiriesCount}
          />
        </div>
        <Modal
          id="TopbarMobileMenu"
          containerClassName={css.modalContainer}
          isOpen={isMobileMenuOpen}
          onClose={this.handleMobileMenuClose}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          {authInProgress ? null : mobileMenu}
        </Modal>

        {/*  RestrictMarketplcaeRequest */}
        <Modal
          id="TopbarMobileMenu.restrictMarketplcaeRequest"
          isOpen={restrictMarketplcaeRequest}
          onClose={() => {
            onRestrictMarketplcaeRequest(false);
          }}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.commingSoonWrapper}>
            <PasswordWallForm
              intl={intl}
              onSubmit={values => {
                console.log(values, 'values');
                const { username, password } = values;
                typeof window != 'undefined' &&
                  localStorage.setItem(
                    'authCred',
                    JSON.stringify({
                      username: username.trim(),
                      password: password.trim(),
                    })
                  );
                onRestrictMarketplcaeRequest(false);
              }}
            />
          </div>
        </Modal>

        {/*  Marketplace - Coming Soon! */}
        <Modal
          id="TopbarMobileMenu.marketplaceCommingSoonRequest"
          isOpen={marketplaceCommingSoonRequest}
          onClose={() => onMarketplaceCommingSoonRequest(false)}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.commingSoonWrapper}>
            <div className={css.heading}>
              <FormattedMessage id="Topbar.marketPlaceComingSonModal" />
            </div>
            <div className={css.descriptionText}>
              <FormattedMessage id="Topbar.marketPlaceComingSonModalDesc" />
            </div>
            <Button
              type="button"
              onClick={e => {
                e.preventDefault();
                onMarketplaceCommingSoonRequest(false);
                history.push(
                  createResourceLocatorString('CircleCommunityPage', routeConfiguration, {}, {})
                );
              }}
            >
              <FormattedMessage id="Topbar.exploreCommunityButton" />
            </Button>
          </div>
        </Modal>

        {/*  Subscription Permissions - Coming Soon! */}
        <Modal
          id="TopbarMobileMenu.subscriptionPermissionModal"
          isOpen={subscriptionPermissionRequest}
          onClose={() => {
            this.restoreScrollPosition();
            onSubscriptionPermissionRequest(false);
          }}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.commingSoonWrapper}>
            <div className={css.heading}>
              {' '}
              <FormattedMessage id="Topbar.subscriptionPermissionTitle" />{' '}
            </div>
            <div className={css.descriptionText}>
              {/* {currentPage == "ListingPage" ?
              <FormattedMessage id="Topbar.subscriptionPermissionMarketplace" /> : */}
              {isAddAnItemPage ? (
                <FormattedMessage id="Topbar.subscriptionPermissionMarketplaceForAddItem" />
              ) : (
                <FormattedMessage id="Topbar.subscriptionPermissionDesc" />
              )}
              {/* <s>
                         <FormattedMessage id="Topbar.subscriptionPermissionPrev" />
                       </s>
                       <FormattedMessage id="Topbar.subscriptionPermissionnewPrice" /> */}
            </div>
            <Button
              type="button"
              onClick={e => {
                e.preventDefault();
                onSubscriptionPermissionRequest(false);
                return history.push(
                  createResourceLocatorString('StripeSubscriptionPage', routeConfiguration, {}, {})
                );
              }}
            >
              <FormattedMessage id="Topbar.upgradeAccountContinue" />
              {/* {isAddAnItemPage ? <FormattedMessage id="Topbar.upgradeAccountContinue" /> : <FormattedMessage id="Topbar.upgradeAccount" />} */}
            </Button>
          </div>
        </Modal>

        {/*  Request Group Permission Modal */}
        <Modal
          id="TopbarMobileMenu.requestGroupPermissionModal"
          isOpen={requestGroupPermissionModal}
          onClose={() => {
            onRequestGroupPermissionModal(false);
          }}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.commingSoonWrapper}>
            <div className={css.heading}>
              <FormattedMessage id="Topbar.requestGroupPermissionTitle" />
            </div>
            <div className={css.descriptionText}>
              <FormattedMessage id="Topbar.requestGroupPermissionDesc" />
            </div>
            <Button
              type="button"
              onClick={e => {
                e.preventDefault();
                onRequestGroupPermissionModal(false);
                return history.push(
                  createResourceLocatorString('StripeSubscriptionPage', routeConfiguration, {}, {})
                );
              }}
            >
              <FormattedMessage id="Topbar.continueButton" />
            </Button>
          </div>
        </Modal>

        {/*  Youth account suspened by guardian */}
        <Modal
          id="TopbarMobileMenu.suspenedInfoModal"
          className={css.logoutModal}
          isOpen={!accountActive}
          onClose={() => {}}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div>
            <span className={css.commingSoonWrapper}>
              <FormattedMessage id="TopbarMobileMenu.suspenedInfo" />
            </span>
            <div className={css.spacerLine}></div>
            <Button className={css.logOutButton} onClick={this.handleLogout}>
              {' '}
              <FormattedMessage id="TopbarDesktop.logout" />
            </Button>
          </div>
        </Modal>

        <Modal
          id="TopbarMobileSearch"
          containerClassName={css.modalContainerSearchForm}
          isOpen={isMobileSearchOpen}
          onClose={this.handleMobileSearchClose}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.searchContainer}>
            <TopbarSearchForm
              onSubmit={this.handleSubmit}
              initialValues={initialSearchFormValues}
              isMobile
              appConfig={config}
            />
            <p className={css.mobileHelp}>
              <FormattedMessage id="Topbar.mobileSearchHelp" />
            </p>
          </div>
        </Modal>
        <ModalMissingInformation
          id="MissingInformationReminder"
          containerClassName={css.missingInformationModal}
          currentUser={currentUser}
          currentUserHasListings={currentUserHasListings}
          currentUserHasOrders={currentUserHasOrders}
          location={location}
          onManageDisableScrolling={onManageDisableScrolling}
          onResendVerificationEmail={onResendVerificationEmail}
          sendVerificationEmailInProgress={sendVerificationEmailInProgress}
          sendVerificationEmailError={sendVerificationEmailError}
        />

        {/* platformRestrictedUser */}
        <Modal
          id="TopbarMobileMenu.platformRestrictedUserModal"
          className={css.logoutModal}
          isOpen={platformRestrictedUser}
          onClose={() => {}}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div>
            <div className={css.platformaccess}>Access restricted</div>
            <div className={css.platformRestrictedUserHead}>
              <FormattedMessage id="TopbarDesktop.platformRestrictedUserHead1" />
              <span
                onClick={e => {
                  e.preventDefault();
                  openIntecomOnClickEvents();
                }}
                className={css.contactUsLink}
              >
                {' '}
                contact{' '}
              </span>
              <FormattedMessage id="TopbarDesktop.platformRestrictedUserHead2" />
            </div>
            <Button className={css.logOutButton} onClick={this.handleLogout}>
              {' '}
              <FormattedMessage id="TopbarDesktop.logout" />
            </Button>
          </div>
        </Modal>

        {/* elephantVerificationModal */}
        <Modal
          id="TopbarMobileMenu.elephantVerificationModal"
          className={css.elephantVerificationModal}
          isOpen={elephantVerificationModal}
          onClose={() => {
            this.restoreScrollPosition();
            onSetElephantVerificationModal(false);
          }}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div>
            <ElephantVerificationForm
              className={css.elephantVerificationForm}
              onSubmit={async values => {
                try {
                  const submitValues = {
                    protectedData: {
                      phoneNumber: values?.phoneNumber,
                    },
                  };
                  await onReevaluateElephantVerification(submitValues);
                  this.restoreScrollPosition();
                  onSetElephantVerificationModal(false);
                } catch (e) {
                  console.log(e, 'e');
                }
              }}
              inProgress={elephantVerificationInProgress}
              formId="ElephantVerificationForm"
              initialValues={{ phoneNumber }}
              currentUser={currentUser}
              elephantVerificationError={elephantVerificationError}
              onSetElephantVerificationModal={onSetElephantVerificationModal}
            />
          </div>
        </Modal>

        {/* Bookmark Modal */}
        <BookmarkModal
          requestBookmarksList={requestBookmarksList}
          isBookmarkListRequested={this.state.isBookmarkListRequested}
          setIsBookmarkListRequested={value => {
            this.setState({ isBookmarkListRequested: value });
          }}
          bookmarksListData={bookmarksListData}
          onDeleteBookMark={onDeleteBookMark}
          setIsViewDetailedSpace={() => {}}
          history={history}
          routeConfiguration={routeConfiguration}
          onManageDisableScrolling={onManageDisableScrolling}
          spaceListData={spaceListData}
          onSetActiveTab={() => {}}
        />

        {/* My Activity Modal */}
        <MyActivityModal
          isMyActivity={this.state.isMyActivityModalOpen}
          onManageDisableScrolling={onManageDisableScrolling}
          onSetActiveTab={() => {
            this.setState({ isMyActivityModalOpen: false });
          }}
          onFetchUserActivity={onFetchUserActivity}
          fetchUserActivityRequest={fetchUserActivityRequest}
          fetchUserActivityData={fetchUserActivityData}
          fetchUserActivityError={fetchUserActivityError}
          routeConfiguration={routeConfiguration}
          history={history}
          currentCommunityMember={currentCommunityMember}
        />

        {/* REQUEST A GROUP */}
        <RequestSpaceModal
          isRequestSpace={isRequestSpace}
          onRequestGroupClick={() => onRequestGroupClick(false)}
          spaceListData={spaceListData}
          requestNewSpace={requestNewSpace}
          currentUser={currentUser}
          onRequestSpace={this.onRequestSpace}
          onManageDisableScrolling={onManageDisableScrolling}
          loading={this.state.loading}
        />

        {/* REQUEST A RULEBOOK  */}
        <Modal
          id="RequestRulebookModal.SectionHero"
          isOpen={this.state.requestRuleBookModal.isModalOpen}
          onClose={() => {
            this.setState({
              requestRuleBookModal: {
                isModalOpen: false,
                inProgress: false,
                isError: null,
              },
            });
          }}
          onManageDisableScrolling={onManageDisableScrolling}
          className={css.mobileNotificationModal}
        >
          <div>
            <div className={css.headingWrapper}>
              <h1>{intl.formatMessage({ id: 'RequestRulebookModal.requestRulebook' })}</h1>

              <h3>
                <IconProfileCard type="info" />
                {intl.formatMessage({ id: 'RequestRulebookModal.requestRulebookSub' })}
              </h3>
            </div>

            <RequestRuleBookForm
              onSubmit={(values, form) => this.requestRuleBookSubmit(values, form)}
              inAddUpdateProgress={this.state.requestRuleBookModal.inProgress}
              intl={intl}
            />
          </div>
        </Modal>

        {/* Notification modal */}
        <NotificationMobileModal
          onFetchNotifications={onFetchNotifications}
          requestNotificationsInProgress={requestNotificationsInProgress}
          notificationsData={notificationsData}
          isOpen={this.state.isOpenNotificationModal || this.props.isOpenNotificationModalRedux}
          setIsOpen={() => {
            this.setState({ isOpenNotificationModal: false });
            this.props.onSetNotificationModalOpen(false);
          }}
          onMarkNotificationRead={onMarkNotificationRead}
          onManageDisableScrolling={onManageDisableScrolling}
          requestNotificationPreferenceInProgress={requestNotificationPreferenceInProgress}
          notificaitonPreferenceData={notificaitonPreferenceData}
          onGetNotificationPreference={onGetNotificationPreference}
          onUpdateNotificationPreference={onUpdateNotificationPreference}
          onMarkAllNotificationsAsRead={onMarkAllNotificationsAsRead}
          onFetchNotificationsCount={onFetchNotificationsCount}
          currentUser={currentUser}
        />

        {/* Complete Business Profile Modal */}
        <CompleteBusinessProfileModal
          isOpen={showBusinessProfileModal}
          onClose={this.handleCloseBusinessProfileModal}
          onManageDisableScrolling={onManageDisableScrolling}
          currentUser={currentUser}
          onImageUpload={this.props.onBusinessProfileImageUpload}
          uploadInProgress={this.props.businessProfileUploadInProgress || false}
          uploadImageError={this.props.businessProfileUploadImageError}
          image={this.props.businessProfileImage}
          onUpdateProfile={this.props.onBusinessProfileUpdate}
          updateInProgress={this.props.businessProfileUpdateInProgress || false}
          updateProfileError={this.props.businessProfileUpdateError}
          onResendVerificationEmail={onResendVerificationEmail}
          sendVerificationEmailInProgress={sendVerificationEmailInProgress}
          sendVerificationEmailError={sendVerificationEmailError}
        />

        <GenericError show={showGenericError} />
      </div>
    );
  }
}

TopbarComponent.defaultProps = {
  className: null,
  rootClassName: null,
  desktopClassName: null,
  mobileRootClassName: null,
  mobileClassName: null,
  notificationCount: 0,
  currentUser: null,
  currentUserHasOrders: null,
  currentPage: null,
  sendVerificationEmailError: null,
  authScopes: [],
};

TopbarComponent.propTypes = {
  className: string,
  rootClassName: string,
  desktopClassName: string,
  mobileRootClassName: string,
  mobileClassName: string,
  isAuthenticated: bool.isRequired,
  isLoggedInAs: bool.isRequired,
  authScopes: array,
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onResendVerificationEmail: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  showGenericError: bool.isRequired,

  // These are passed from Page to keep Topbar rendering aware of location changes
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const { rootClassName, ...rest } = props;
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...rest}
    />
  );
};

export default Topbar;
